import {
  Button,
  UNSTABLE_Toast as ToastAria,
  UNSTABLE_ToastContent as ToastContentAria,
} from "react-aria-components";
import type { ComponentProps, ReactNode } from "react";
import type { QueuedToast } from "react-aria-components";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import CloseIconDark from "@/assets/images/icons2/icon--close.svg";
import CloseIconLight from "@/assets/images/icons2/icon--close-white.svg";
import { cn } from "@/lib/helpers/cn";
import ErrorIcon from "@/assets/images/icons2/icon--error.svg?component";
import SuccessIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import WarningIcon from "@/assets/images/icons2/icon--warning.svg?component";

import "./Toast.styles.css";

export type ToastConfig = {
  heading: string;
  text?: ReactNode;
  variant?: VariantProps<typeof toastStyles>["variant"];
};

type ToastProps = {
  // Explicit close handler rather than slot="close". React Aria's slot fires
  // state.close() synchronously inside the React event handler, which races
  // with startViewTransition and prevents the exit animation from playing.
  // The provider passes closeToast() here instead, which wraps the full
  // queue.close() call inside startViewTransition before any state mutation.
  onClose?: () => void;
  toast: QueuedToast<ToastConfig>;
} & ComponentProps<typeof ToastAria> &
  VariantProps<typeof toastStyles>;

export function Toast2({ onClose, toast, ...toastProps }: ToastProps) {
  const { heading, text, variant } = toast.content;
  const { body, close, content, icon } = toastStyles({ variant });
  const Icon = ToastIconMap.get(variant);

  return (
    <ToastAria toast={toast} {...toastProps} className={cn("react-aria-Toast", body())}>
      <figure className={cn(icon(), !!text && "tw:self-start")}>{Icon}</figure>

      <ToastContentAria className={cn("react-aria-ToastContent", content())}>
        {heading && (
          <strong className="tw:text-sm tw:leading-6 tw:font-bold" slot="title">
            {heading}
          </strong>
        )}

        {text && (
          <p className="tw:text-sm" slot="description">
            {text}
          </p>
        )}
      </ToastContentAria>

      {/* No slot="close" — that would call state.close() synchronously inside
          the React event handler, mutating the queue store before
          startViewTransition can snapshot the old DOM. onClose from the
          provider wraps queue.close() in its own transition instead. */}
      <Button aria-label="Close" className={cn("toast-close", close())} onPress={onClose}>
        <img alt="" src={variant === "warning" ? CloseIconDark : CloseIconLight} />
      </Button>
    </ToastAria>
  );
}

const toastStyles = tv({
  defaultVariants: {
    variant: "success",
  },
  slots: {
    body: cn("tw:flex tw:items-start tw:gap-2 tw:p-2", "tw:rounded-md tw:shadow-md"),
    close: "tw:opacity-50 tw:hover:opacity-100",
    content: "tw:flex tw:flex-col tw:w-full tw:transition-opacity tw:duration-300",
    icon: "tw:m-0! tw:flex",
    loadingText: "tw:absolute tw:inset-0 tw:transition-opacity tw:duration-300",
  },
  variants: {
    variant: {
      error: {
        body: "tw:bg-fill-error-primary",
        content: "tw:text-text-white",
        icon: "tw:text-text-white",
      },
      success: {
        body: "tw:bg-fill-success-primary",
        content: "tw:text-text-white",
        icon: "tw:text-text-white",
      },
      warning: {
        body: "tw:bg-fill-warning-secondary",
        content: "tw:text-text-warning-primary",
        icon: "tw:text-text-primary",
      },
    },
  },
});

const ToastIconMap = new Map([
  ["error", <ErrorIcon />],
  ["success", <SuccessIcon />],
  ["warning", <WarningIcon />],
]);
